import * as React from "react";
import { useState, useEffect, useContext, useCallback } from "react";
import { Link, useLocation, useHistory, useParams } from "react-router-dom";

// react-bootstrap components
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Spinner,
  Form,
  Table,
} from "react-bootstrap";
import { Modal, ModalBody, ModalFooter } from "reactstrap";

import { NotificationAlertContext } from "contexts/notificationAlertContext";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination.js";
import { copyToClipboard } from "../../utilities/commonUtilities";
import ActivateClient from "../Clients/ActivateClient.js";

import {
  getLicensesAndEntitlements,
  getEntitlementUsages,
  addLicense,
  retrieveLicenseKey,
  revokeLicenseKey,
} from "graphql/queries.js";
import { API, graphqlOperation } from "aws-amplify";
import SweetAlert from "react-bootstrap-sweetalert";
import _ from "lodash";
import {
  tourGuideSteps,
  licenseKeyModalWithoutKeySteps,
  licenseKeyModalWithKeySteps,
} from "views/ManageLicensing/ManageLicensingSteps";
import { AuthContext } from "contexts/authContext";
import { useTour } from "@reactour/tour";

function ManageLicensing(props) {
  // Contexts
  const notify = useContext(NotificationAlertContext);
  const currentUser = useContext(AuthContext);
  const history = useHistory();

  const { clientId } = useParams();

  const groupName = currentUser.groupName;
  // States
  const [licenseData, setLicenseData] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [entitlementUsage, setEntitlementUsage] = useState([]);
  const [licenseKeyName, setLicenseKeyName] = useState(null);
  const [licenseKey, setLicenseKey] = useState(null);

  const [showLoader, setShowLoader] = useState(true);

  const headers = [
    { key: "entitlement_type", label: "Entitlement Type" },
    { key: "client_environment_name", label: "Client Environment Name" },
    { key: "channel_name", label: "Channel Name" },
  ];

  // Tour Guide
  const { setIsOpen, setCurrentStep, setSteps } = useTour();
  const [licenseKeyModalSteps, setLicenseKeyModalSteps] = useState(
    licenseKeyModalWithoutKeySteps
  );

  const [alert, setAlert] = useState(null);

  const [addLicenseKeyActionModalOpen, setAddLicenseKeyActionModalOpen] =
    useState(false);
  const [keyDetailsActionModalOpen, setKeyDetailsActionModalOpen] =
    useState(false);
  const [licenseEnvironments, setLicenseEnvironments] = useState([]);

  const [licenseKeyDetails, setLicenseKeyDetails] = useState({
    license_key_name: "",
    license_key_id: "",
  });
  const [retrieveLicenseKeyValue, setRetrieveLicenseKeyValue] = useState("");
  const [licenseStatus, setLicenseStatus] = useState(false);
  const [clientName, setClientName] = useState("");
  const [loadingLicense, setLoadingLicense] = useState(false);
  // Pagination Starts
  const columnMapping = {
    "Entitlement Type": "entitlement_type",
    "Client Environment": "client_environment_name",
    "Channel Name": "channel_name",
  };

  const [totalCount, setTotalCount] = useState(0);
  const numberOfRowsData = [25, 50, 100];

  const [currentLimit, setCurrentLimit] = useState(numberOfRowsData[1]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
  const [sortValue, setSortValue] = useState("last_updated");

  const setStateForPagnination = async (key, value) => {
    if (key === "currentLimit") {
      setCurrentLimit(value);
    } else if (key === "offset") {
      setCurrentOffset(value);
    } else if (key === "ascOrDesc") {
      setAscOrDescValue(value);
    } else {
      setSortValue(value);
    }
  };
  // Pagination Ends

  const fetchLicensesAndEntitlements = async (variables = {}) => {
    try {
      const params = {
        client_id: clientId,
      };
      variables = { ...variables, ...params };
      const response = await API.graphql(
        graphqlOperation(getLicensesAndEntitlements, variables)
      );
      if (response.data?.getLicensesAndEntitlements?.success) {
        const entitlements =
          response.data.getLicensesAndEntitlements.body.Entitlements;
        const clientName =
          response.data.getLicensesAndEntitlements.body.client_name;
        setEntitlements(entitlements);
        setClientName(clientName);
        const licenses = response.data.getLicensesAndEntitlements.body.Licenses;
        setLicenseData(licenses);
        licenses.map((x) => {
          if (!x.revoked) {
            setLicenseStatus(true);
          }
        });
      } else {
        console.error(response.data?.getLicensesAndEntitlements?.message);
      }
      return response.data?.getLicensesAndEntitlements?.success;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const fetchEntitlementUsages = async (
    variables = {},
    isCSVDownload = false
  ) => {
    try {
      const params = {
        client_id: clientId,
      };
      const response = await API.graphql(
        graphqlOperation(getEntitlementUsages, params)
      );
      if (response.data?.getEntitlementUsages?.success) {
        if (!isCSVDownload) {
          const entitlementUsages =
            response.data.getEntitlementUsages.body.EntitlementUsages;
          setEntitlementUsage(entitlementUsages);
          const totalCount = response.data.getEntitlementUsages.body.totalCount;
          setTotalCount(totalCount);
        } else {
          const filter = {
            "Client Ids": params.client_ids,
          };
          const messageDataForCSV = _.get(
            response,
            "data.getEntitlementUsages.body.EntitlementUsage"
          );
          return { messageDataForCSV, filter };
        }
      } else {
        console.error(response.data?.getEntitlementUsages?.message);
      }
      return response.data?.getEntitlementUsages?.success;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const fetchPageData = async () => {
    setAddLicenseKeyActionModalOpen(false);
    setShowLoader(true);
    const filterObj = {
      offSet: 0,
      limit: currentLimit,
    };
    const promiseAllResults = await Promise.all([
      fetchLicensesAndEntitlements(filterObj),
      fetchEntitlementUsages(filterObj),
    ]);
    const showPage = promiseAllResults.every((value) => value);
    if (!showPage) {
      notify("danger");
      history.goBack();
    }
    setShowLoader(false);
  };

  const retrieveLicenseKeyFunction = async () => {
    try {
      setLoadingLicense(true);
      const params = {
        client_id: clientId,
        license_key_id: licenseKeyDetails.license_key_id,
      };
      const response = await API.graphql(
        graphqlOperation(retrieveLicenseKey, params)
      );
      if (response.data?.retrieveLicenseKey?.success) {
        setRetrieveLicenseKeyValue(
          response.data?.retrieveLicenseKey?.body?.keygen_key
        );
        setLicenseKeyModalSteps(licenseKeyModalWithKeySteps);
      } else {
        console.error(response.data?.retrieveLicenseKey?.message);
        notify("danger");
      }
    } catch (error) {
      console.error(error);
      notify("danger");
    } finally {
      setLoadingLicense(false);
    }
  };

  const revokeLicenseKeyFunction = async () => {
    try {
      setShowLoader(true);
      const params = {
        client_id: clientId,
        license_key_id: licenseKeyDetails.license_key_id,
      };
      const response = await API.graphql(
        graphqlOperation(revokeLicenseKey, params)
      );
      if (response.data?.revokeLicenseKey?.success) {
        setKeyDetailsActionModalOpen(false);
        await fetchPageData();
        notify("success", "Successfully revoked license key.");
      } else {
        console.error(response.data?.revokeLicenseKey?.message);
        notify("danger");
      }
    } catch (error) {
      console.error(error);
      notify("danger");
    } finally {
      setShowLoader(false);
    }
  };

  const handleRevokeLicenseKey = () => {
    setAlert(
      <SweetAlert
        showCancel
        title={"Are you sure?"}
        onConfirm={async () => {
          await revokeLicenseKeyFunction();
          setAlert(null);
        }}
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="primary"
        cancelBtnBsStyle="danger"
        confirmBtnText="Confirm"
        cancelBtnText="Cancel"
      >
        You want to revoke the license? This action is irreversible
      </SweetAlert>
    );
  };

  useEffect(() => {
    fetchPageData();
  }, []);

  if (showLoader) return <Spinner animation="grow" />;

  return (
    <>
      {alert}
      <Container fluid>
        <Row>
          <Col>
            <h2>Manage Licensing</h2>
          </Col>
          <Col className="my-auto text-right">
            {(groupName === "internal" ||
              groupName === "internal_readonly") && (
              <Button
                variant="primary"
                onClick={() => {
                  setAddLicenseKeyActionModalOpen(true);
                }}
              >
                <i className="fas fa-plus"></i> Add License Key
              </Button>
            )}
            {currentUser.groupName === "external" && (
              <Button
                variant="secondary"
                className="btn-outline ml-4"
                onClick={() => {
                  setSteps(tourGuideSteps);
                  setCurrentStep(0);
                  setIsOpen(true);
                }}
              >
                <i className="fas fa-desktop"></i> View Our Tour Guide
              </Button>
            )}
          </Col>
        </Row>
        <Row>
          <Col md="12" id="license-details-header">
            <h5>Client Name: {clientName}</h5>
            <h5>Client Id: {clientId}</h5>
            <h5>License Status: {licenseStatus ? "True" : "False"}</h5>
          </Col>
        </Row>
        {(groupName === "internal" || groupName === "internal_readonly") && (
          <Link
            className="btn btn-primary btn-outline"
            to={{
              pathname: `/admin/clients/${clientId}/manage-licensing/manage-entitlements`,
            }}
          >
            <i className="fas fa-list"></i> Manage Entitlements
          </Link>
        )}
        <Row>
          <Col md="12">
            <h4>Licenses</h4>
            <Card id="license-data-grid">
              <Card.Body className="table-responsive p-0">
                <>
                  <Table className="table-hover table-striped w-full">
                    <thead>
                      <tr>
                        <th>License Key Name</th>
                        <th>Valid</th>
                        <th id="view-details-column">View Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {licenseData.map((licenses) => {
                        return (
                          <tr>
                            <td>{licenses?.license_key_name}</td>
                            <td>{licenses?.revoked ? "False" : "True"}</td>
                            <td>
                              <button
                                className="btn btn-primary"
                                style={{
                                  position: "relative",
                                  left: "20px",
                                }}
                                onClick={() => {
                                  setKeyDetailsActionModalOpen(true);
                                  setLicenseKeyModalSteps(
                                    licenseKeyModalWithoutKeySteps
                                  );
                                  setLicenseKeyDetails(licenses);
                                  setLicenseEnvironments(
                                    licenses?.environments
                                  );
                                  setRetrieveLicenseKeyValue("");
                                }}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <br></br>
        <Row>
          <Col md="12">
            <h4>Entitlements</h4>
            <Card id="entitlement-data-grid">
              <Card.Body className="table-responsive p-0">
                <>
                  <Table className="table-hover table-striped w-full">
                    <thead>
                      <tr>
                        <th>Entitlement</th>
                        <th>Allotments</th>
                        <th>Amount Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entitlements.map((licenses) => {
                        return (
                          <tr>
                            <td>{licenses?.entitlement_type}</td>
                            <td>{licenses?.allotment}</td>
                            <td>{licenses?.total_usage}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <br></br>
        <Row>
          <Col md="12" id="entitlement-usage-data-grid">
            <h4>Entitlement Usages</h4>
            <ReactTable
              data={entitlementUsage}
              totalCount={totalCount}
              functionCallBack={fetchEntitlementUsages}
              makePaginationStatic={true}
              setStateForPagnination={setStateForPagnination}
              currentLimit={currentLimit}
              currentOffset={currentOffset}
              ascOrDescValue={ascOrDescValue}
              sortValue={sortValue}
              numberOfRowsData={numberOfRowsData}
              columnMapping={columnMapping}
              tableName="Entitlement Usages"
              CSVHeaders={headers}
              CSVFileName="Entitlement_Usages"
              notify={notify}
              columns={[
                {
                  Header: "Entitlement Type",
                  accessor: "entitlement_type",
                  filterable: false,
                  showTooltip: true,
                  sortable: true,
                },
                {
                  Header: "Client Environment Name",
                  accessor: "client_environment_name",
                  filterable: false,
                  showTooltip: true,
                  sortable: true,
                },
                {
                  Header: "Channel Name",
                  accessor: "channel_name",
                  filterable: false,
                  showTooltip: true,
                  sortable: true,
                },
              ]}
            />
          </Col>
        </Row>

        <Modal
          toggle={() =>
            setAddLicenseKeyActionModalOpen(!addLicenseKeyActionModalOpen)
          }
          size="xl"
          isOpen={addLicenseKeyActionModalOpen}
          scrollable={false}
        >
          <div className="modal-header">
            <div
              className="modal-title"
              id="clientEnvironmentActionOptionsList"
            >
              <h5>Add License Key</h5>
            </div>
            <button
              aria-label="Close"
              className="close"
              type="button"
              onClick={() => setAddLicenseKeyActionModalOpen(false)}
            >
              <span aria-hidden={true}>x</span>
            </button>
          </div>
          <ActivateClient
            addLicense={true}
            clientId={clientId}
            clientName={clientName}
            setShowLoader={setShowLoader}
            fetchPageData={fetchPageData}
          />
        </Modal>

        <Modal
          toggle={() =>
            setKeyDetailsActionModalOpen(!keyDetailsActionModalOpen)
          }
          size="lg"
          isOpen={keyDetailsActionModalOpen}
          scrollable={false}
        >
          <div className="modal-header">
            <button
              aria-label="Close"
              className="close"
              type="button"
              onClick={() => setKeyDetailsActionModalOpen(false)}
            >
              <span aria-hidden={true}>x</span>
            </button>
          </div>
          <ModalBody>
            <Row>
              <Col>
                <h5>{licenseKeyDetails?.license_key_name} Details</h5>
              </Col>
              {currentUser.groupName === "external" && (
                <Col className="my-auto text-right">
                  <Button
                    variant="secondary"
                    className="btn-outline"
                    onClick={() => {
                      setSteps(licenseKeyModalSteps);
                      setCurrentStep(0);
                      setIsOpen(true);
                    }}
                  >
                    <i className="fas fa-desktop"></i> View Our Tour Guide
                  </Button>
                </Col>
              )}
            </Row>
            {licenseEnvironments && licenseEnvironments.length && (
              <Table className="table-hover table-striped w-full">
                <thead>
                  <tr>
                    <th>Client Environment Id</th>
                    <th>Client Environment Name</th>
                    <th>Client Environment Type</th>
                    <th>Environment Status</th>
                  </tr>
                </thead>
                <tbody>
                  {licenseEnvironments?.map((environment) => {
                    return (
                      <tr>
                        <td>{environment?.client_environment_id}</td>
                        <td>{environment?.client_environment_name}</td>
                        <td>
                          {+environment?.client_environment_type === 0
                            ? "Fully Managed"
                            : "Remote"}
                        </td>
                        <td>{environment?.deploy_status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
            {loadingLicense && (
              <div>
                <Spinner style={{ position: "initial" }} animation="grow" />
              </div>
            )}
            {!loadingLicense && retrieveLicenseKeyValue.length !== 0 && (
              <div>
                <Form.Control
                  id="license-key-text-box"
                  type="text"
                  autoFocus
                  style={{ width: "90%", display: "unset", margin: "10px 0px" }}
                  value={retrieveLicenseKeyValue}
                  disabled
                  minLength="1"
                  maxLength="80"
                  onChange={(e) => setRetrieveLicenseKeyValue(e.target.value)}
                  name="Retrieve License Key"
                />
                <Button
                  variant="primary"
                  className="btn-outline"
                  id="license-key-copy-button"
                  style={{ position: "relative", left: "20px" }}
                  onClick={() => {
                    copyToClipboard(retrieveLicenseKeyValue);
                  }}
                >
                  <i className="fas fa-copy"></i>
                </Button>
              </div>
            )}
            {groupName === "internal" && !licenseKeyDetails?.revoked && (
              <button
                className="btn btn-danger btn-outline"
                style={{ marginRight: "10px" }}
                onClick={() => {
                  handleRevokeLicenseKey();
                }}
              >
                Revoke License Key
              </button>
            )}
            <button
              id="retrieve-license-key-button"
              className="btn btn-primary"
              onClick={() => {
                retrieveLicenseKeyFunction();
              }}
            >
              Retrieve License Key
            </button>
          </ModalBody>
        </Modal>
      </Container>
    </>
  );
}

export default ManageLicensing;
